import React, { useRef, useState, useEffect, useCallback } from 'react';
import { QrCode, ImagePlus, X, Link, Upload, Settings, Type, Palette, Star } from 'lucide-react';
import QRCode from 'qrcode';
import type { StampType, AddStampOptions } from '../hooks/useStamps';
import type { UseDrawCanvasReturn } from '../hooks/useDrawCanvas';
import PaintPopover from './PaintPopover';
import { fetchStampPresets, type StampPresetItem } from '../../../services/api';
import StampManager from '../../StampManager';
import TextPopover from './TextPopover';
import StampShapePopover from './StampShapePopover';
import { ExclusivePopover } from '../hooks/useExclusivePopover';

interface StampToolbarProps {
    onAddStamp: (type: StampType, imageDataUrl?: string, options?: AddStampOptions) => void;
    isPaintMode: boolean;
    onTogglePaint: () => void;
    draw: UseDrawCanvasReturn;
    onRequestEyedropper?: (callback: (color: string) => void) => void;
    /** popover 展開方向；toolbar 在右側時用 'right'，預設 'left'；橫向時用 'bottom' */
    popoverPlacement?: 'left' | 'right' | 'bottom';
    /** 工具列方向：'vertical' 為直向（桌面），'horizontal' 為橫向（手機） */
    orientation?: 'vertical' | 'horizontal';
}

type QrTab = 'url' | 'upload';

const StampToolbar: React.FC<StampToolbarProps> = ({
    onAddStamp, isPaintMode, onTogglePaint, draw, onRequestEyedropper, popoverPlacement, orientation = 'vertical',
}) => {
    const isHorizontal = orientation === 'horizontal';
    // 預設 placement：直向 → 'left'；橫向 → 'bottom'
    const effectivePlacement = popoverPlacement ?? (isHorizontal ? 'bottom' : 'left');
    const isBottom = effectivePlacement === 'bottom';
    // 'bottom' 改用 fixed 從畫面底部彈出（手機友善），避開 toolbar 容器寬度限制與切割
    const popoverPlacementClass =
        effectivePlacement === 'left' ? 'absolute right-full mr-2 top-0 w-64' :
        effectivePlacement === 'right' ? 'absolute left-full ml-2 top-0 w-64' :
        'fixed left-4 right-4 bottom-4 mx-auto max-w-md';
    const [shapePopoverOpen, setShapePopoverOpen] = useState(false);
    const [textPopoverOpen, setTextPopoverOpen] = useState(false);
    const [qrPopoverOpen, setQrPopoverOpen] = useState(false);
    const [qrTab, setQrTab] = useState<QrTab>('url');
    const [qrText, setQrText] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const qrUploadRef = useRef<HTMLInputElement>(null);
    const uploadRef = useRef<HTMLInputElement>(null);

    // 從 DB 載入啟用中的印章預設（只取使用者自訂，排除內建特殊行為 + 內建愛心/星星）
    const [presets, setPresets] = useState<StampPresetItem[]>([]);
    const loadPresets = useCallback(() => {
        fetchStampPresets()
            .then(all => setPresets(all.filter(p => p.enabled).sort((a, b) => a.sortOrder - b.sortOrder)))
            .catch(console.error);
    }, []);
    useEffect(() => { loadPresets(); }, [loadPresets]);

    const handleCloseSettings = useCallback(() => {
        setShowSettings(false);
        loadPresets();
    }, [loadPresets]);

    const handleQrGenerate = async () => {
        if (!qrText.trim()) return;
        setQrLoading(true);
        try {
            const dataUrl = await QRCode.toDataURL(qrText.trim(), {
                width: 300,
                margin: 1,
                color: { dark: '#000000', light: '#ffffff' },
            });
            onAddStamp('qrcode', dataUrl);
            setQrPopoverOpen(false);
            setQrText('');
        } catch (e) {
            console.error('QR generate failed', e);
        } finally {
            setQrLoading(false);
        }
    };

    const handleQrUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            onAddStamp('qrcode', reader.result as string);
            setQrPopoverOpen(false);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            onAddStamp('upload', reader.result as string);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    /** 點擊自訂印章 → 使用其 imageUrl 作為印章 */
    const handleCustomPresetClick = async (preset: StampPresetItem) => {
        const url = preset.imageUrl || preset.builtinDataUrl;
        if (!url) return;
        if (url.startsWith('http')) {
            try {
                const res = await fetch(url);
                const blob = await res.blob();
                const reader = new FileReader();
                reader.onload = () => onAddStamp('upload', reader.result as string);
                reader.readAsDataURL(blob);
            } catch (e) {
                console.error('載入印章圖片失敗', e);
            }
        } else {
            onAddStamp('upload', url);
        }
    };

    // 橫向（手機）按鈕加大到 44px 符合觸控可點擊面積
    const iconBtn = `${isHorizontal ? 'w-11 h-11' : 'w-9 h-9'} flex items-center justify-center rounded-xl transition-all text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700`;
    const iconBtnActive = 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
    // tooltip 與 divider 依方向切換：橫向時提示出現在按鈕下方、divider 為垂直線
    const tooltip = isHorizontal
        ? 'pointer-events-none absolute top-full mt-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-slate-800 dark:bg-slate-700 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-50'
        : 'pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-800 dark:bg-slate-700 text-white text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-50';
    const divider = isHorizontal
        ? <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 mx-0.5" />
        : <div className="w-5 h-px bg-slate-200 dark:bg-slate-700 my-0.5" />;

    // 使用者自訂印章：排除所有內建 icon 行為（Heart/Star 已改成「一般印章」popover 提供；QrCode/ImagePlus 自有特殊行為）
    const customPresets = presets.filter(p =>
        !p.builtinDataUrl && !['QrCode', 'ImagePlus', 'Heart', 'Star'].includes(p.icon || '')
    );

    return (
        <div className={`relative flex items-center bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl ${isHorizontal ? 'flex-row gap-2 px-3 py-2 w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>div]:shrink-0' : 'flex-col gap-1.5 py-2 px-1 shrink-0'}`}>
            {/* 1. 一般印章（形狀印章 popover） */}
            <div className="relative group">
                <button
                    className={`${iconBtn} ${shapePopoverOpen ? iconBtnActive : ''}`}
                    onClick={() => setShapePopoverOpen(v => !v)}
                >
                    <Star size={18} />
                </button>
                <span className={tooltip}>加入印章</span>
                {shapePopoverOpen && (
                    <StampShapePopover
                        onSubmit={(dataUrl, shapeMeta) => onAddStamp('upload', dataUrl, { shapeMeta })}
                        onClose={() => setShapePopoverOpen(false)}
                        onRequestEyedropper={onRequestEyedropper}
                        placement={effectivePlacement}
                    />
                )}
            </div>

            {/* 2. 文字印章 */}
            <div className="relative group">
                <button
                    className={`${iconBtn} ${textPopoverOpen ? iconBtnActive : ''}`}
                    onClick={() => setTextPopoverOpen(v => !v)}
                >
                    <Type size={16} />
                </button>
                <span className={tooltip}>加入文字</span>
                {textPopoverOpen && (
                    <TextPopover
                        onSubmit={(dataUrl, textData) => onAddStamp('text', dataUrl, { textData })}
                        onClose={() => setTextPopoverOpen(false)}
                        onRequestEyedropper={onRequestEyedropper}
                        placement={effectivePlacement}
                    />
                )}
            </div>

            {/* 3. 畫筆工具 */}
            <div className="relative group">
                <button
                    className={`${iconBtn} ${isPaintMode ? iconBtnActive : ''}`}
                    onClick={onTogglePaint}
                >
                    <Palette size={16} />
                </button>
                <span className={tooltip}>畫筆工具</span>
                {isPaintMode && (
                    <PaintPopover draw={draw} onClose={onTogglePaint} placement={effectivePlacement} />
                )}
            </div>

            {divider}

            {/* 4. QR Code */}
            <div className="relative group">
                <button
                    className={`${iconBtn} ${qrPopoverOpen ? iconBtnActive : ''}`}
                    onClick={() => setQrPopoverOpen(v => !v)}
                >
                    <QrCode size={18} />
                </button>
                <span className={tooltip}>加入 QR Code</span>
                {qrPopoverOpen && (
                  <ExclusivePopover onClose={() => setQrPopoverOpen(false)}>
                    {isBottom && (
                        <div
                            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
                            onClick={() => setQrPopoverOpen(false)}
                        />
                    )}
                    <div
                        className={`${popoverPlacementClass} z-50 ${isBottom ? 'animate-in slide-in-from-bottom-4 duration-200 pt-2' : 'max-w-[calc(100vw-2rem)]'} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-3 flex flex-col gap-3`}
                        onClick={e => e.stopPropagation()}
                    >
                        {isBottom && (
                            <div className="flex justify-center -mt-1">
                                <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">加入 QR Code</span>
                            <button
                                className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                                onClick={() => setQrPopoverOpen(false)}
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-0.5 gap-0.5 text-xs">
                            <button
                                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg font-medium transition-all ${qrTab === 'url' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                                onClick={() => setQrTab('url')}
                            >
                                <Link size={11} /> 輸入網址
                            </button>
                            <button
                                className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg font-medium transition-all ${qrTab === 'upload' ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}
                                onClick={() => setQrTab('upload')}
                            >
                                <Upload size={11} /> 上傳圖片
                            </button>
                        </div>
                        {qrTab === 'url' && (
                            <>
                                <input
                                    autoFocus
                                    type="text"
                                    value={qrText}
                                    onChange={e => setQrText(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleQrGenerate(); }}
                                    placeholder="輸入網址或文字..."
                                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                                <button
                                    disabled={!qrText.trim() || qrLoading}
                                    onClick={handleQrGenerate}
                                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5"
                                >
                                    {qrLoading ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
                                    生成 QR Code
                                </button>
                            </>
                        )}
                        {qrTab === 'upload' && (
                            <button
                                onClick={() => qrUploadRef.current?.click()}
                                className="w-full py-2.5 border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all flex flex-col items-center gap-1"
                            >
                                <Upload size={16} />
                                點擊上傳 QR Code 圖片
                            </button>
                        )}
                        <input
                            ref={qrUploadRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleQrUpload}
                        />
                    </div>
                  </ExclusivePopover>
                )}
            </div>

            {/* 5. 上傳圖片印章 */}
            <div className="relative group">
                <button
                    className={iconBtn}
                    onClick={() => uploadRef.current?.click()}
                >
                    <ImagePlus size={18} />
                </button>
                <span className={tooltip}>上傳圖片印章</span>
            </div>
            <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
            />

            {/* 6. 使用者自訂的印章 */}
            {customPresets.length > 0 && (
                <>
                    {divider}
                    {customPresets.map(preset => (
                        <div key={preset.id} className="relative group">
                            <button
                                className={iconBtn}
                                onClick={() => handleCustomPresetClick(preset)}
                            >
                                {preset.imageUrl ? (
                                    <img src={preset.imageThumbnailUrl || preset.imageUrl} alt={preset.name} className="w-5 h-5 object-contain rounded" />
                                ) : (
                                    <span className="text-[10px]">{preset.name.charAt(0)}</span>
                                )}
                            </button>
                            <span className={tooltip}>{preset.name}</span>
                        </div>
                    ))}
                </>
            )}

            {/* 7. 印章設定 */}
            {divider}
            <div className="relative group">
                <button
                    className={`${iconBtn} ${showSettings ? iconBtnActive : ''}`}
                    onClick={() => setShowSettings(true)}
                >
                    <Settings size={16} />
                </button>
                <span className={tooltip}>印章設定</span>
            </div>

            {/* 印章設定 Modal */}
            {showSettings && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
                    onClick={handleCloseSettings}
                >
                    <div
                        className="bg-slate-50 dark:bg-slate-950 rounded-2xl shadow-2xl w-[90vw] max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
                            <span className="font-bold text-slate-800 dark:text-white text-sm">印章設定</span>
                            <button
                                onClick={handleCloseSettings}
                                className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5">
                            <StampManager />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StampToolbar;
